/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react'
import { feelsForBucket, BUCKETS } from '../../services/feelsApi'

// FAST Feels chat. Search or browse-by-mood (paged, compact), tap a Feel and it
// drops straight into chat where it renders as the finished Feel — the share/
// preview render with its lyrics already overlaid, at the feel's true aspect.

// /v1 `Feel` accessors: media.{video,poster} plus a reduced "W:H" aspect string
// (there is no raw pixel dimensions field on /v1 — the ratio is all layout needs).
const videoOf = (feel) => feel?.media?.video
const imageOf = (feel) => feel?.media?.poster
const dimsOf = (feel) => {
  const m = /^(\d+):(\d+)$/.exec(feel?.media?.aspect || '')
  return m ? [Number(m[1]), Number(m[2])] : [9, 16]
}
const artistOf = (feel) => feel?.artist?.name || ''
const titleOf = (feel) => `${artistOf(feel)} — ${feel?.song?.name || ''}`

const COLORS = {
  border: '#d9d9e3',
  card: '#ffffff',
  bg: '#f7f7fa',
  accent: '#6c4ce0',
  muted: '#6b6b76',
}

const sectionStyle = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  background: COLORS.card,
  padding: 14,
  marginBottom: 16,
}

const headingStyle = {
  margin: 0,
  fontSize: 15,
  fontWeight: 700,
  color: '#1c1c24',
}

const PAGE_SIZE = 8

// Compact browse thumbnail — poster, plays muted on hover, click adds to chat.
function ThumbCell({ feel, onPick }) {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      onClick={() => onPick(feel)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={`Add to chat · ${titleOf(feel)}`}
      style={{
        position: 'relative',
        padding: 0,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        overflow: 'hidden',
        cursor: 'pointer',
        background: '#000',
      }}
    >
      <div style={{ position: 'relative', width: '100%', aspectRatio: '1 / 1', background: '#000' }}>
        <img
          src={imageOf(feel)}
          alt=""
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: hover ? 0 : 1,
            transition: 'opacity 120ms',
          }}
        />
        {hover && (
          <video
            src={videoOf(feel)}
            muted
            loop
            autoPlay
            playsInline
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            padding: '3px 5px',
            fontSize: 10,
            lineHeight: 1.2,
            color: '#fff',
            background: 'linear-gradient(transparent, rgba(0,0,0,0.78))',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {artistOf(feel)}
        </div>
      </div>
    </button>
  )
}

// Chat message rendered as the compiled Feel itself (share/preview render with
// lyrics overlaid), at the feel's real aspect. Only the ACTIVE message plays
// (with sound) — every other message is a static poster, so just one Feel ever
// plays/sounds at a time. Tapping a poster makes it the active one.
function FeelMessage({ feel, unmuted, onToggle }) {
  const ref = useRef(null)
  const dims = dimsOf(feel)
  const vertical = dims[1] >= dims[0]
  const width = vertical ? 210 : 300

  // Every chat Feel plays (muted); exactly one may be unmuted at a time. Toggling
  // sound is a user gesture, so the browser allows the unmuted play().
  useEffect(() => {
    const v = ref.current
    if (!v) return
    v.muted = !unmuted
    if (unmuted) {
      const p = v.play()
      if (p && p.catch) p.catch(() => {})
    } else {
      // Pause muted Feels so we don't keep many videos decoding in the
      // background (CPU/GPU); they resume from the start when unmuted.
      v.pause()
      v.currentTime = 0
    }
  }, [unmuted])

  return (
    <div
      onClick={onToggle}
      title={unmuted ? 'Tap to mute' : 'Tap for sound'}
      style={{
        position: 'relative',
        width,
        maxWidth: '100%',
        aspectRatio: `${dims[0]} / ${dims[1]}`,
        borderRadius: 12,
        overflow: 'hidden',
        background: '#000',
        cursor: 'pointer',
        boxShadow: '0 2px 10px rgba(0,0,0,0.16)',
      }}
    >
      <video
        ref={ref}
        key={feel.id}
        src={videoOf(feel)}
        autoPlay
        loop
        muted={!unmuted}
        playsInline
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          padding: '2px 8px',
          borderRadius: 999,
          background: 'rgba(0,0,0,0.6)',
          color: '#fff',
          fontSize: 13,
        }}
      >
        {unmuted ? '\u{1F50A}' : '\u{1F507}'}
      </div>
    </div>
  )
}

export default function FeelsDrawerPreview() {
  const [bucket, setBucket] = useState(BUCKETS[0])
  const [page, setPage] = useState(0)
  const [chat, setChat] = useState([])
  const [activeKey, setActiveKey] = useState(null)
  const [feels, setFeels] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Feels are fetched from the external Feels Sharing API (feelsApi →
  // integration-partner-api). No internal feels source and no fallback: a
  // failure here is shown as a failure so we know the integration is broken.
  useEffect(() => {
    let active = true
    setLoading(true)
    setError(null)
    setPage(0)
    feelsForBucket(bucket)
      .then((res) => {
        if (active) {
          setFeels(Array.isArray(res) ? res : [])
          setLoading(false)
        }
      })
      .catch((e) => {
        if (active) {
          setFeels([])
          setError(e.message || 'Failed to load Feels from the external API.')
          setLoading(false)
        }
      })
    return () => {
      active = false
    }
  }, [bucket])

  const pageCount = Math.max(1, Math.ceil(feels.length / PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const pageItems = feels.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE)

  // Add a Feel to chat and make it the unmuted one (most-recently-selected plays
  // with sound; everything else mutes). Tapping the unmuted Feel mutes it.
  const addToChat = (feel) => {
    const key = `${feel.id}-${Date.now()}-${Math.floor(Math.random() * 1e6)}`
    setChat((prev) => [...prev, { ...feel, key }])
    setActiveKey(key)
  }

  const toggleMute = (key) => setActiveKey((cur) => (cur === key ? null : key))

  const pill = (label, active, onClick) => (
    <button
      type="button"
      key={label}
      onClick={onClick}
      style={{
        padding: '5px 12px',
        border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
        background: active ? COLORS.accent : '#fff',
        color: active ? '#fff' : '#444',
        borderRadius: 999,
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: 12.5,
      }}
    >
      {label}
    </button>
  )

  return (
    <div style={{ padding: 20, maxWidth: 760, background: COLORS.bg, minHeight: '100%' }}>
      <h2 style={{ margin: '0 0 4px' }}>Feels Chat</h2>
      <p style={{ color: COLORS.muted, fontSize: 13, marginTop: 0, marginBottom: 14 }}>
        Browse Feels by mood and tap one to drop it into chat.
      </p>

      {/* ── Browse (mood buckets, compact, paged) ──────────── */}
      <section style={sectionStyle}>
        <h3 style={{ ...headingStyle, marginBottom: 10 }}>Browse</h3>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {BUCKETS.map((b) => pill(b, b === bucket, () => setBucket(b)))}
        </div>

        {loading ? (
          <p style={{ color: COLORS.muted, fontStyle: 'italic', margin: 0 }}>Loading Feels…</p>
        ) : error ? (
          <p style={{ color: '#c0392b', margin: 0, fontSize: 13 }}>⚠️ {error}</p>
        ) : pageItems.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 8,
            }}
          >
            {pageItems.map((feel) => (
              <ThumbCell key={feel.id} feel={feel} onPick={addToChat} />
            ))}
          </div>
        ) : (
          <p style={{ color: COLORS.muted, fontStyle: 'italic', margin: 0 }}>No Feels here.</p>
        )}

        {pageCount > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginTop: 12 }}>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              style={{
                padding: '4px 12px',
                borderRadius: 999,
                border: `1px solid ${COLORS.border}`,
                background: '#fff',
                cursor: safePage === 0 ? 'default' : 'pointer',
                opacity: safePage === 0 ? 0.4 : 1,
                fontSize: 13,
              }}
            >
              ‹ Prev
            </button>
            <span style={{ fontSize: 12.5, color: COLORS.muted }}>
              {safePage + 1} / {pageCount}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={safePage >= pageCount - 1}
              style={{
                padding: '4px 12px',
                borderRadius: 999,
                border: `1px solid ${COLORS.border}`,
                background: '#fff',
                cursor: safePage >= pageCount - 1 ? 'default' : 'pointer',
                opacity: safePage >= pageCount - 1 ? 0.4 : 1,
                fontSize: 13,
              }}
            >
              Next ›
            </button>
          </div>
        )}
      </section>

      {/* ── Chat (bounded; renders Feels as compiled Feels) ── */}
      <section style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={headingStyle}>Chat</h3>
          {chat.length > 0 && (
            <button
              type="button"
              onClick={() => setChat([])}
              style={{
                fontSize: 12,
                color: COLORS.muted,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
          )}
        </div>
        {chat.length > 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              maxHeight: 460,
              overflowY: 'auto',
              paddingRight: 4,
            }}
          >
            {chat.map((feel) => (
              <div key={feel.key} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <FeelMessage
                  feel={feel}
                  unmuted={feel.key === activeKey}
                  onToggle={() => toggleMute(feel.key)}
                />
                <div style={{ fontSize: 11.5, color: COLORS.muted }}>{titleOf(feel)}</div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: COLORS.muted, fontStyle: 'italic', margin: 0 }}>
            Nothing here yet. Tap a Feel above to drop it in.
          </p>
        )}
      </section>
    </div>
  )
}
